import React, { useState } from 'react';
import { SearchedOpinion, SearchedParagraph } from '../types';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { getCaseTitle } from '../utils/titleUtils';

interface OpinionDetailCardProps {
  opinion: SearchedOpinion;
  compact?: boolean; // For simplified view
}

const ExpandableText: React.FC<{ text: string; maxLines?: number }> = ({ text, maxLines = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const lineHeight = 1.5; // rem
  const maxHeight = `${maxLines * lineHeight}rem`;

  return (
    <div className="md:col-span-2">
      <span className="font-semibold text-gray-700">Full Text:</span>
      <div className="mt-2 relative">
        <div
          className={`text-gray-600 whitespace-pre-wrap overflow-hidden transition-all duration-300 ${
            !isExpanded ? 'line-clamp-3' : ''
          }`}
          style={!isExpanded ? { maxHeight } : undefined}
        >
          {text}
        </div>
        {text.split('\n').length > maxLines || text.length > 300 ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show more
              </>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ParagraphsSection: React.FC<{ paragraphs: SearchedParagraph[]; compact?: boolean }> = ({ paragraphs, compact }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!paragraphs || paragraphs.length === 0) return null;

  const droppedCount = paragraphs.filter(p => p.dropReason).length;

  return (
    <div className={`${compact ? '' : 'md:col-span-2'} mt-4 pt-4 border-t border-gray-300`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`font-semibold text-gray-700 ${compact ? 'text-xs' : ''}`}>Source Paragraphs:</span>
          <span className={`px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium ${compact ? 'text-xs' : 'text-xs'}`}>
            {paragraphs.length} {paragraphs.length === 1 ? 'paragraph' : 'paragraphs'}
          </span>
          {droppedCount > 0 && (
            <span className={`px-2 py-1 bg-orange-100 text-orange-700 rounded font-medium ${compact ? 'text-xs' : 'text-xs'}`} title="Paragraphs excluded from AI processing due to bad OCR quality">
              ⚠️ {droppedCount} dropped
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors ${compact ? 'text-xs' : 'text-sm'}`}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show
            </>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {paragraphs.map((paragraph, idx) => (
            <div key={idx} className={`bg-white border rounded p-3 ${paragraph.dropReason ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-500">Paragraph {idx + 1}</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                  Score: {paragraph.score.toFixed(2)}
                </span>
                {paragraph.dropReason && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium" title={`Drop reason: ${paragraph.dropReason}`}>
                    ⚠️ Dropped: {paragraph.dropReason.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              <p className={`whitespace-pre-wrap ${compact ? 'text-xs' : 'text-sm'} ${paragraph.dropReason ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{paragraph.text.length > 500 && paragraph.dropReason ? paragraph.text.substring(0, 200) + '... [truncated - bad OCR]' : paragraph.text}</p>
              {paragraph.aiSummary && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <span className="text-xs font-semibold text-purple-700">AI Summary:</span>
                  <p className={`text-gray-600 mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>{paragraph.aiSummary}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OpinionDetailCard: React.FC<OpinionDetailCardProps> = ({ opinion, compact = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (compact) {
    // Compact view for SimplifiedProviderColumn
    // Always generate title from plaintiffs and defendants (ignore opinion.title)
    const generatedTitle = getCaseTitle(opinion.plaintiffs, opinion.defendants);
    const displayTitle = generatedTitle || opinion.caseNumber || `Opinion ID: ${opinion.id}`;
    
    return (
      <div data-component="OpinionDetailCard" className="border border-gray-200 rounded-lg bg-gray-50">
        {/* Opinion Header - Always Visible */}
        <div className="p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-blue-600 mb-1">
                {displayTitle}
              </div>
              {displayTitle !== opinion.caseNumber && opinion.caseNumber && (
                <div className="text-xs text-gray-500">
                  {opinion.caseNumber}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {opinion.dropReason && (
                <span className="px-2 py-1 rounded font-medium text-xs bg-orange-100 text-orange-700" title={`This opinion was excluded from AI processing: ${opinion.dropReason.replace(/_/g, ' ')}`}>
                  ⚠️ Dropped
                </span>
              )}
              {opinion.polarity && (
                <span className={`px-2 py-1 rounded font-medium text-xs ${
                  opinion.polarity === 'contrary' ? 'bg-red-100 text-red-700' :
                  opinion.polarity === 'mixed' ? 'bg-amber-100 text-amber-700' :
                  opinion.polarity === 'supporting' ? 'bg-emerald-100 text-emerald-700' : ''
                }`}>
                  {opinion.polarity === 'contrary' ? '⚖️ Contrary' :
                   opinion.polarity === 'mixed' ? '⚖️ Mixed' :
                   opinion.polarity === 'supporting' ? '✓ Supporting' : ''}
                </span>
              )}
              {opinion.score && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium text-xs">
                  {Math.round(opinion.score * 100)}%
                </span>
              )}
              {opinion.link && (
                <a
                  href={opinion.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-blue-600 hover:text-blue-700 transition-colors"
                  title="View on Portal"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* CNS Summary - Always Visible */}
          {opinion.summary && (
            <div className="text-xs text-gray-700 mb-2">
              <span className="font-semibold">CNS Summary:</span> {opinion.summary}
            </div>
          )}

          {/* AI Analysis - Always Visible */}
          {opinion.aiSummary && (
            <div className="text-xs text-gray-700 mb-2">
              <span className="font-semibold text-purple-700">AI Analysis:</span> {opinion.aiSummary}
            </div>
          )}

          {/* Court and Location - Single Line */}
          <div className="text-xs text-gray-600">
            {opinion.court && <span><span className="font-semibold">Court:</span> {opinion.court}</span>}
            {opinion.court && (opinion.city || opinion.state || opinion.location) && <span>, </span>}
            {opinion.location ? (
              <span><span className="font-semibold">Location:</span> {opinion.location}</span>
            ) : (
              <>
                {(opinion.city || opinion.state) && <span><span className="font-semibold">Location:</span> </span>}
                {opinion.city && <span>{opinion.city}</span>}
                {opinion.city && opinion.state && <span>, </span>}
                {opinion.state && <span>{opinion.state}</span>}
              </>
            )}
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-3 pb-3 pt-0 border-t border-gray-200 bg-white">
            <div className="space-y-2 text-xs mt-3">
              {opinion.caseNumber && (
                <div>
                  <span className="font-semibold text-gray-700">Case Number: </span>
                  <span className="text-gray-600">{opinion.caseNumber}</span>
                </div>
              )}
              {opinion.plaintiffs && opinion.plaintiffs.length > 0 && (
                <div>
                  <span className="font-semibold text-gray-700">Plaintiffs: </span>
                  <span className="text-gray-600">{opinion.plaintiffs.join(', ')}</span>
                </div>
              )}
              {opinion.defendants && opinion.defendants.length > 0 && (
                <div>
                  <span className="font-semibold text-gray-700">Defendants: </span>
                  <span className="text-gray-600">{opinion.defendants.join(', ')}</span>
                </div>
              )}
              {opinion.categories && opinion.categories.length > 0 && (
                <div>
                  <span className="font-semibold text-gray-700">Categories: </span>
                  <span className="text-gray-600">{opinion.categories.join(', ')}</span>
                </div>
              )}
              <div>
                <span className="font-semibold text-gray-700">ID: </span>
                <span className="text-gray-600">{opinion.id}</span>
              </div>
              
              <ParagraphsSection paragraphs={opinion.paragraphs} compact={true} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full view for OpinionDetailsModal
  // Always generate title from plaintiffs and defendants (ignore opinion.title)
  const generatedTitle = getCaseTitle(opinion.plaintiffs, opinion.defendants);
  const displayTitle = generatedTitle || opinion.caseNumber || `Opinion ID: ${opinion.id}`;
  
  return (
    <div data-component="OpinionDetailCard" className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      {/* Header with ID and Portal Link */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-300">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-blue-600">
              {displayTitle}
            </h3>
            {opinion.polarity && (
              <span className={`px-2 py-1 rounded font-medium text-xs ${
                opinion.polarity === 'contrary' ? 'bg-red-100 text-red-700' :
                opinion.polarity === 'mixed' ? 'bg-amber-100 text-amber-700' :
                opinion.polarity === 'supporting' ? 'bg-emerald-100 text-emerald-700' : ''
              }`}>
                {opinion.polarity === 'contrary' ? '⚖️ Contrary' :
                 opinion.polarity === 'mixed' ? '⚖️ Mixed' :
                 opinion.polarity === 'supporting' ? '✓ Supporting' : ''}
              </span>
            )}
          </div>
          {displayTitle !== opinion.caseNumber && opinion.caseNumber && (
            <div className="text-sm text-gray-500 mt-1">
              {opinion.caseNumber}
            </div>
          )}
        </div>
        <a
          href={opinion.link}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          View on Portal
        </a>
      </div>

      {/* Metadata Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        {opinion.caseNumber && (
          <div>
            <span className="font-semibold text-gray-700">Case Number:</span>
            <span className="ml-2 text-gray-600">{opinion.caseNumber}</span>
          </div>
        )}

        {opinion.fillingDate && (
          <div>
            <span className="font-semibold text-gray-700">Filing Date:</span>
            <span className="ml-2 text-gray-600">{opinion.fillingDate}</span>
          </div>
        )}

        {opinion.plaintiffs && opinion.plaintiffs.length > 0 && (
          <div className="md:col-span-2">
            <span className="font-semibold text-gray-700">Plaintiffs:</span>
            <span className="ml-2 text-gray-600">{opinion.plaintiffs.join(', ')}</span>
          </div>
        )}

        {opinion.defendants && opinion.defendants.length > 0 && (
          <div className="md:col-span-2">
            <span className="font-semibold text-gray-700">Defendants:</span>
            <span className="ml-2 text-gray-600">{opinion.defendants.join(', ')}</span>
          </div>
        )}

        {opinion.judge && (
          <div>
            <span className="font-semibold text-gray-700">Judge:</span>
            <span className="ml-2 text-gray-600">{opinion.judge}</span>
          </div>
        )}

        {opinion.court && (
          <div>
            <span className="font-semibold text-gray-700">Court:</span>
            <span className="ml-2 text-gray-600">{opinion.court}</span>
          </div>
        )}

        {opinion.courthouseName && (
          <div>
            <span className="font-semibold text-gray-700">Courthouse:</span>
            <span className="ml-2 text-gray-600">{opinion.courthouseName}</span>
          </div>
        )}

        {opinion.subCourt && (
          <div>
            <span className="font-semibold text-gray-700">Sub-Court:</span>
            <span className="ml-2 text-gray-600">{opinion.subCourt}</span>
          </div>
        )}

        {opinion.state && (
          <div>
            <span className="font-semibold text-gray-700">State:</span>
            <span className="ml-2 text-gray-600">{opinion.state}</span>
          </div>
        )}

        {opinion.city && (
          <div>
            <span className="font-semibold text-gray-700">City:</span>
            <span className="ml-2 text-gray-600">{opinion.city}</span>
          </div>
        )}

        {opinion.location && (
          <div>
            <span className="font-semibold text-gray-700">Location:</span>
            <span className="ml-2 text-gray-600">{opinion.location}</span>
          </div>
        )}

        {opinion.cause && (
          <div className="md:col-span-2">
            <span className="font-semibold text-gray-700">Cause:</span>
            <span className="ml-2 text-gray-600">{opinion.cause}</span>
          </div>
        )}

        {opinion.categories && opinion.categories.length > 0 && (
          <div className="md:col-span-2">
            <span className="font-semibold text-gray-700">Categories:</span>
            <span className="ml-2 text-gray-600">{opinion.categories.join(', ')}</span>
          </div>
        )}

        {opinion.summary && (
          <div className="md:col-span-2">
            <span className="font-semibold text-gray-700">CNS Summary:</span>
            <p className="mt-1 text-gray-600">{opinion.summary}</p>
          </div>
        )}

        {opinion.aiSummary && (
          <div className="md:col-span-2">
            <span className="font-semibold text-purple-700">AI Analysis:</span>
            <p className="mt-1 text-gray-900">{opinion.aiSummary}</p>
          </div>
        )}

        {opinion.fullText && (
          <ExpandableText text={opinion.fullText} maxLines={3} />
        )}

        {opinion.score && (
          <div>
            <span className="font-semibold text-gray-700">Relevance Score:</span>
            <span className="ml-2 text-gray-600">{opinion.score.toFixed(2)}</span>
          </div>
        )}

        <ParagraphsSection paragraphs={opinion.paragraphs} />
      </div>
    </div>
  );
};
